const VISIBLE_CLASS = 'menu--visible';

interface MenuInstance {
  button: HTMLButtonElement;
  menu: HTMLElement;
}

function isElement(el: EventTarget | null): el is Element {
  return el instanceof Element;
}

function normaliseLabel(el: Element): string {
  const label =
    (el.getAttribute('data-label') ||
      el.getAttribute('aria-label') ||
      el.textContent ||
      '')
      .trim()
      .toLowerCase();
  return label;
}

export class MenuButtons {
  private static openInstance: MenuInstance | null = null;
  private static initialised = false;
  private static typeaheadBuffer = '';
  private static typeaheadTimeout: number | null = null;
  private static root: Document | HTMLElement | null = null;
  private static handleTriggerClick: ((event: Event) => void) | null = null;
  private static handleTriggerKeyDown: EventListener | null = null;
  private static handleDocumentPointerDown: ((event: PointerEvent) => void) | null = null;
  private static handleDocumentKeyDown: ((event: KeyboardEvent) => void) | null = null;
  private static handleMenuItemClick: ((event: MouseEvent) => void) | null = null;
  private static handleRecalcPosition: (() => void) | null = null;

  static init(root: Document | HTMLElement = document): void {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (this.initialised) return;
    this.initialised = true;
    this.root = root;

    const handleTriggerClick = (event: Event) => {
      const target = isElement(event.target)
        ? event.target.closest<HTMLButtonElement>('.menu-button[data-menu-target]')
        : null;
      if (!target) return;
      event.preventDefault();
      this.toggle(target);
    };

    const handleTriggerKeyDown = (event: Event) => {
      if (!(event instanceof KeyboardEvent)) return;
      if (!isElement(event.target)) return;
      const trigger = event.target.closest<HTMLButtonElement>('.menu-button[data-menu-target]');
      if (!trigger) return;

      if (event.defaultPrevented) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const instance = this.open(trigger, { focus: false });
        if (!instance) return;
        const items = this.items(instance.menu);
        if (!items.length) return;
        const index = event.key === 'ArrowDown' ? 0 : items.length - 1;
        this.setActiveItem(items, index);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.toggle(trigger);
      }
    };

    const handleDocumentPointerDown = (event: PointerEvent) => {
      const instance = this.openInstance;
      if (!instance) return;
      const { button, menu } = instance;
      if (!isElement(event.target)) return;
      if (button.contains(event.target) || menu.contains(event.target)) return;
      this.close(button, { focusButton: true });
    };

    const handleDocumentKeyDown = (event: KeyboardEvent) => {
      const instance = this.openInstance;
      if (!instance) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(instance.button, { focusButton: true });
        return;
      }
      if (event.key === 'Tab') {
        this.close(instance.button, { focusButton: false });
        return;
      }

      const items = this.items(instance.menu);
      if (!items.length) return;

      const currentIndex = items.findIndex((el) => el.getAttribute('data-active') === 'true');

      const move = (nextIndex: number) => {
        this.setActiveItem(items, nextIndex);
      };

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const idx = currentIndex >= 0 ? (currentIndex + 1) % items.length : 0;
        move(idx);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        const idx = currentIndex >= 0 ? (currentIndex - 1 + items.length) % items.length : items.length - 1;
        move(idx);
      } else if (event.key === 'Home') {
        event.preventDefault();
        move(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        move(items.length - 1);
      } else if (event.key === 'Enter' || event.key === ' ') {
        if (currentIndex >= 0) {
          event.preventDefault();
          items[currentIndex].click();
        }
      } else if (event.key.length === 1 && !event.altKey && !event.ctrlKey && !event.metaKey) {
        const char = event.key.toLowerCase();
        if (!char.match(/^[\p{Letter}\p{Number}]$/u)) return;
        const search = this.consumeTypeahead(char);
        const start = currentIndex >= 0 ? currentIndex + 1 : 0;
        const ordered = [...items.slice(start), ...items.slice(0, start)];
        const match = ordered.find((el) => normaliseLabel(el).startsWith(search));
        if (match) {
          event.preventDefault();
          this.setActiveItem(items, items.indexOf(match));
        }
      }
    };

    const handleMenuItemClick = (event: MouseEvent) => {
      const instance = this.openInstance;
      if (!instance) return;
      if (!isElement(event.target)) return;
      const item = event.target.closest('[role="menuitem"]');
      if (!item) return;
      if (!instance.menu.contains(item)) return;
      this.close(instance.button, { focusButton: true });
    };

    const recalcPosition = () => {
      if (!this.openInstance) return;
      this.position(this.openInstance);
    };

    root.addEventListener('click', handleTriggerClick);
    root.addEventListener('keydown', handleTriggerKeyDown, true);
    document.addEventListener('pointerdown', handleDocumentPointerDown, true);
    document.addEventListener('keydown', handleDocumentKeyDown);
    document.addEventListener('click', handleMenuItemClick);
    window.addEventListener('resize', recalcPosition, { passive: true });
    document.addEventListener('scroll', recalcPosition, { passive: true, capture: true });

    this.handleTriggerClick = handleTriggerClick;
    this.handleTriggerKeyDown = handleTriggerKeyDown;
    this.handleDocumentPointerDown = handleDocumentPointerDown;
    this.handleDocumentKeyDown = handleDocumentKeyDown;
    this.handleMenuItemClick = handleMenuItemClick;
    this.handleRecalcPosition = recalcPosition;
  }

  static destroy(): void {
    if (!this.initialised) return;
    if (this.openInstance) {
      this.close(this.openInstance.button, { focusButton: false });
    }

    const root = this.root ?? document;

    if (this.handleTriggerClick) {
      root.removeEventListener('click', this.handleTriggerClick);
    }
    if (this.handleTriggerKeyDown) {
      root.removeEventListener('keydown', this.handleTriggerKeyDown, true);
    }
    if (this.handleDocumentPointerDown) {
      document.removeEventListener('pointerdown', this.handleDocumentPointerDown, true);
    }
    if (this.handleDocumentKeyDown) {
      document.removeEventListener('keydown', this.handleDocumentKeyDown);
    }
    if (this.handleMenuItemClick) {
      document.removeEventListener('click', this.handleMenuItemClick);
    }
    if (this.handleRecalcPosition) {
      window.removeEventListener('resize', this.handleRecalcPosition);
      document.removeEventListener('scroll', this.handleRecalcPosition, { capture: true });
    }

    this.handleTriggerClick = null;
    this.handleTriggerKeyDown = null;
    this.handleDocumentPointerDown = null;
    this.handleDocumentKeyDown = null;
    this.handleMenuItemClick = null;
    this.handleRecalcPosition = null;
    this.root = null;
    this.initialised = false;
  }

  static closeByMenuId(id: string): void {
    if (!this.openInstance) return;
    if (this.openInstance.menu.id !== id) return;
    this.close(this.openInstance.button);
  }

  static toggle(button: HTMLButtonElement): void {
    const expanded = button.getAttribute('aria-expanded') === 'true';
    if (expanded) {
      this.close(button, { focusButton: false });
    } else {
      this.open(button);
    }
  }

  static open(button: HTMLButtonElement, options: { focus?: boolean } = {}): MenuInstance | null {
    const { focus = true } = options;
    const targetSelector = button.dataset.menuTarget?.trim();
    if (!targetSelector) return null;

    let menu: HTMLElement | null = null;

    const simpleIdMatch = targetSelector.match(/^#([^\s>+~.,\[\]]+)$/);
    if (simpleIdMatch) {
      const rawId = simpleIdMatch[1];
      menu = document.getElementById(rawId) as HTMLElement | null;

      if (!menu) {
        const selector =
          typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
            ? `#${CSS.escape(rawId)}`
            : `#${rawId}`;

        try {
          menu = document.querySelector<HTMLElement>(selector);
        } catch (error) {
          console.warn('Invalid menu selector', targetSelector, error);
          return null;
        }
      }
    } else {
      try {
        menu = document.querySelector<HTMLElement>(targetSelector);
      } catch (error) {
        console.warn('Invalid menu selector', targetSelector, error);
        return null;
      }
    }

    if (!menu) return null;

    if (this.openInstance && this.openInstance.button !== button) {
      this.close(this.openInstance.button, { focusButton: false });
    }

    if (!menu.id) {
      console.warn('Menu element is missing an id attribute:', menu);
    }

    button.setAttribute('aria-expanded', 'true');
    if (menu.getAttribute('role') !== 'menu') {
      menu.setAttribute('role', 'menu');
    }
    menu.hidden = false;
    menu.classList.add(VISIBLE_CLASS);

    this.openInstance = { button, menu };
    this.position(this.openInstance);

    const items = this.items(menu);
    if (items.length) {
      this.setActiveItem(items, 0, { scroll: false });
      if (focus) {
        items[0].focus({ preventScroll: true });
      }
    } else if (focus) {
      menu.focus?.({ preventScroll: true });
    }

    return this.openInstance;
  }

  static close(button: HTMLButtonElement, options: { focusButton?: boolean } = {}): void {
    const { focusButton = true } = options;
    const instance = this.openInstance;
    if (!instance || instance.button !== button) return;

    instance.menu.hidden = true;
    instance.menu.classList.remove(VISIBLE_CLASS);
    instance.menu.querySelectorAll('[data-active="true"]').forEach((el) => el.removeAttribute('data-active'));
    button.setAttribute('aria-expanded', 'false');
    this.openInstance = null;
    if (focusButton) {
      button.focus({ preventScroll: true });
    }
  }

  static closeOpenMenu(): void {
    if (this.openInstance) {
      this.close(this.openInstance.button);
    }
  }

  private static items(menu: HTMLElement): HTMLElement[] {
    const items = Array.from(menu.querySelectorAll<HTMLElement>('[role="menuitem"]')).filter(
      (el) => !el.hasAttribute('disabled') && !el.getAttribute('aria-disabled')
    );
    items.forEach((item) => {
      if (item.tabIndex === 0) {
        item.tabIndex = -1;
      }
    });
    return items;
  }

  private static setActiveItem(items: HTMLElement[], index: number, options: { scroll?: boolean } = {}): void {
    const { scroll = true } = options;
    items.forEach((el) => el.removeAttribute('data-active'));
    const target = items[index];
    if (!target) return;
    target.setAttribute('data-active', 'true');
    if (scroll && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ block: 'nearest' });
    }
    if (document.activeElement !== target) {
      target.focus({ preventScroll: true });
    }
  }

  private static consumeTypeahead(char: string): string {
    window.clearTimeout(this.typeaheadTimeout ?? undefined);
    this.typeaheadBuffer += char;
    this.typeaheadTimeout = window.setTimeout(() => {
      this.typeaheadBuffer = '';
    }, 500);
    return this.typeaheadBuffer;
  }

  private static position(instance: MenuInstance): void {
    const { button, menu } = instance;
    const rect = button.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = document.documentElement.clientHeight;

    const margin = 6;
    let top = rect.bottom + margin;

    const wasHidden = menu.hidden;
    if (wasHidden) {
      menu.hidden = false;
      menu.style.visibility = 'hidden';
    }

    const menuWidth = menu.offsetWidth;
    const menuHeight = menu.offsetHeight;

    const align = button.dataset.align === 'start' ? 'start' : 'end';
    let left = align === 'start' ? rect.left : rect.right - menuWidth;

    if (top + menuHeight > viewportHeight && rect.top - margin - menuHeight >= 0) {
      top = rect.top - margin - menuHeight;
    }

    left = Math.max(8, Math.min(left, viewportWidth - menuWidth - 8));

    menu.style.top = `${Math.round(top)}px`;
    menu.style.left = `${Math.round(left)}px`;
    menu.style.right = 'auto';
    menu.style.bottom = 'auto';
    menu.style.position = 'fixed';

    if (wasHidden) {
      menu.style.visibility = '';
      menu.hidden = true;
    }
  }
}

// Convenience for tests
export function __resetMenuButtonsForTests(): void {
  MenuButtons.destroy();
  (MenuButtons as any).openInstance = null;
  (MenuButtons as any).initialised = false;
  (MenuButtons as any).typeaheadBuffer = '';
  if (typeof window !== 'undefined') {
    window.clearTimeout((MenuButtons as any).typeaheadTimeout ?? undefined);
    (MenuButtons as any).typeaheadTimeout = null;
  }
}
